import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { COMPANY, SUPPORT_EMAIL } from '../data/launch';

/**
 * INTERNAL NOTE — not rendered.
 * Outstanding items for legal sign-off are tracked in
 * docs/internal-pre-launch-checklist.md: exact retention periods, named
 * processors, contact-matching implementation, minimum age, and international
 * transfer wording. Do not surface those uncertainties to visitors.
 */
export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-609" data-mp="198mhrwp6jzac">
      <SEO title="Privacy Policy | KittyPot" description="How KittyPot Ltd collects, uses and protects your personal data in the KittyPot app and on kittypot.com, written in plain UK English." ogTitle="KittyPot Privacy Policy" ogDescription="How KittyPot handles your personal data." path="/privacy-policy" data-id="element-610" data-mp="157u5eul0he9u" />
      <Navbar data-id="element-611" data-mp="a0nfd6bpp26b" />
      <main className="pt-32 pb-24" data-id="element-612" data-mp="9z2llix81n9i">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-613" data-mp="a26xz3kubpb9">
          <h1 className="text-4xl font-black text-kitty-dark mb-4" data-id="element-614" data-mp="3rfkkf4nzbl99">
            Privacy Policy
          </h1>
          <p className="text-gray-500 font-medium mb-10" data-id="element-615" data-mp="8zsliq3o9b6">
            Last updated: 26 August 2026
          </p>

          <div className="prose prose-gray max-w-none text-gray-600 space-y-6" data-id="element-616" data-mp="3iema64my6hi8">
            <p data-id="element-617" data-mp="3nfs4g6w9actv">
              This policy explains what personal data {COMPANY.legalName}{' '}
              collects when you use the KittyPot app or our website, why we
              collect it, and what rights you have. It is written in plain UK
              English.
            </p>

            <p data-id="element-618" data-mp="16rg0wjhezmfh">
              KittyPot helps groups record shared expenses, split costs and keep
              track of who owes what. KittyPot does not hold or move your money.
              Group members settle up directly with each other.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-619" data-mp="1597l84ujc93p">
              1. Who we are
            </h2>
            <p data-id="element-620" data-mp="109l6ts2z0mtb">
              {COMPANY.legalName} (company number {COMPANY.registrationNumber})
              is the data controller for the personal data described in this
              policy. You can reach us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-621" data-mp="1sxquonpbmrxd">
                {SUPPORT_EMAIL}
              </a>{' '}
              or by post:
            </p>
            <address className="not-italic" data-id="element-622" data-mp="1f6c8y9414cmm">
              {COMPANY.addressLines.map(line => <React.Fragment key={line} data-id="element-623" data-mp="2bqbgjl6lrwro">
                  {line}
                  <br data-id="element-624" data-mp="26ytesuwr9s5n" />
                </React.Fragment>)}
            </address>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-625" data-mp="3plb8szih9ph7">
              2. Account information
            </h2>
            <p data-id="element-626" data-mp="3ujmina8rvzzf">
              When you create a KittyPot account we process the information
              needed to identify you and keep your account secure. Depending on
              how you sign up, this may include:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-627" data-mp="7gzu8b794l07">
              <li data-id="element-628" data-mp="cfkuubqf16p6">your name or display name</li>
              <li data-id="element-629" data-mp="ccowwnekii0q">your email address</li>
              <li data-id="element-630" data-mp="hdcjmwvrkcp5">a phone number, where you choose to provide one</li>
              <li data-id="element-631" data-mp="hbjejm0su0j9">a profile picture, where you choose to add one</li>
              <li data-id="element-632" data-mp="m54cxf6b0wiz">
                authentication and account identifiers, including identifiers
                supplied by Google, Apple or Facebook if you sign in with one of
                those services
              </li>
            </ul>
            <p data-id="element-633" data-mp="10442qq5jzhgq">
              If you sign in with Google, Apple or Facebook, that provider tells
              us enough to identify your account. We do not receive your
              password, and we do not post anything to those accounts.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-634" data-mp="hi0qegjeeopx">
              3. Kitty and expense information
            </h2>
            <p data-id="element-635" data-mp="r8degiqf900t">
              To provide the core service we process the content you and other
              members add to a kitty:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-636" data-mp="3jkemb6oi80x">
              <li data-id="element-637" data-mp="3upc02x9igwhe">kitty names and the members of each kitty</li>
              <li data-id="element-638" data-mp="3ur6lecop2qm0">expenses, including description, amount and date</li>
              <li data-id="element-639" data-mp="dhqcmhpd6u0i">who paid, and who an expense is shared with</li>
              <li data-id="element-640" data-mp="8i5f8bpuetpw">split information, whether equal or custom amounts</li>
              <li data-id="element-641" data-mp="8k8d1hx8lr56">balances between members</li>
              <li data-id="element-642" data-mp="1ov2jl1gf7sqw">
                settlement records, including amounts marked as paid and
                confirmations that a payment was received
              </li>
            </ul>
            <p data-id="element-643" data-mp="1rg0jil2jniy9">
              This information is shared with the other members of that kitty,
              because a shared expense record only works if the group can see
              it. It is not made public.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-644" data-mp="1ih6o2y0y8yma">
              4. Contact matching
            </h2>
            <p data-id="element-645" data-mp="1mjjmf1dd9u91">
              If you give the app permission to access your contacts, we use
              that access only to check whether people you already know are on
              KittyPot, so you can invite them more easily. Contact identifiers
              may be submitted to KittyPot for that matching.
            </p>
            <p data-id="element-646" data-mp="25g2zg1rak2av">
              We do not build a public directory, and we do not let anyone
              browse a list of all KittyPot users. You can use KittyPot without
              granting contacts permission, and you can withdraw the permission
              at any time in your device settings.
            </p>
            {/* INTERNAL: confirm whether contact identifiers are hashed and
                discarded after matching, then describe that precisely here. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-647" data-mp="33p6dzli23xn8">
              5. Receipts
            </h2>
            <p data-id="element-648" data-mp="2tt3aocbgxsyp">
              You can optionally attach a receipt image to an expense. Where
              receipt scanning is available, an image may be processed to read
              details such as the amount so they can be suggested when you add
              the expense. Receipt images and any details taken from them are
              stored as part of the expense record so the group can refer back
              to them.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-649" data-mp="2b3wp8hjain97">
              6. Bank details
            </h2>
            <p data-id="element-650" data-mp="265czs75hmtc3">
              You may choose to save bank details, such as an account name, sort
              code and account number, so they are easy to share when your group
              settles up. This is entirely optional. We store these details so
              that you can share them, and we do not publish them, sell them or
              share them automatically without you choosing to.
            </p>
            <p data-id="element-651" data-mp="25xhro6vex1ft">
              KittyPot does not use these details to make payments. Any payment
              between group members happens directly between you, outside
              KittyPot.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-652" data-mp="1d8kl310kct2w">
              7. Currency preferences
            </h2>
            <p data-id="element-653" data-mp="1i6vvbcurt5ln">
              Where you record expenses in more than one currency, we process
              your currency preferences and any conversion figures you supply so
              amounts can be displayed consistently in your kitty. Converted
              amounts shown in the app are for presentation and may not match a
              bank or provider rate at the time you settle.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-654" data-mp="28pkp46rs36db">
              8. Device information and notifications
            </h2>
            <p data-id="element-655" data-mp="2imz5y7hm5x65">
              To deliver and support the app we may process technical
              information such as your device type and operating system, the app
              version you are running, and a push notification token if you turn
              notifications on. We also process basic diagnostic information so
              we can find and fix problems.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-656" data-mp="2nk90u2nrv11d">
              9. Reporting an issue
            </h2>
            <p data-id="element-657" data-mp="2wdvjqs6vs9t5">
              If you report a problem, we process what you send us: your
              description of the issue, any screenshot you choose to attach, and
              diagnostic details such as your internal user identifier, device
              type, app version and error information. We use this only to
              investigate and resolve the issue.
            </p>
            <p data-id="element-658" data-mp="3tzoodrpjzwlx">
              The same applies to anything else you send us. If you email
              support, we keep that correspondence so we can help you and refer
              back to it if you contact us again. Please do not send us
              passwords or full bank details, and do not include them in a
              screenshot.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-659" data-mp="3692sszwqk4st">
              10. How you found us, and location
            </h2>
            <p data-id="element-660" data-mp="2x9xu9tl6cbgn">
              We may record privacy-friendly information about how an account
              was created, such as an invite or campaign code, the registration
              source, and the platform or store you installed from. This helps
              us understand which routes into KittyPot work well. None of this
              involves tracking where you are.
            </p>
            <p data-id="element-661" data-mp="3a003fjrtxcfp">
              Our website runs no location-tracking or analytics scripts. Where
              the app uses a device permission such as location, your device
              will always ask you first, and you can review or withdraw any
              permission at any time in your device settings.
            </p>
            <p data-id="element-662" data-mp="351ost6tpmxwf">
              Like any internet service, our systems receive your IP address
              when your device connects to us. It is used to deliver the service
              and to keep it secure. An IP address can indicate an approximate
              region, such as a country or city, and where it is used in that
              way it is only ever at that coarse level, never a precise
              position.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-663" data-mp="3nxeuesxqds9s">
              11. Analytics
            </h2>
            <p data-id="element-664" data-mp="kaekz8mcc17z">
              Where we use analytics, it is to understand how KittyPot is used
              in aggregate, such as which features are used and where people
              encounter problems, so we can improve the product. Our website
              currently runs no analytics or advertising scripts at all; the
              technologies it does use are set out in our Cookie Policy.
            </p>
            {/* INTERNAL: name the analytics providers actually in use once
                confirmed. The website itself currently loads none. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-665" data-mp="1m27pg0ju8w7b">
              12. Why we process your data, and our lawful bases
            </h2>
            <ul className="list-disc pl-5 space-y-2" data-id="element-666" data-mp="1v35lnwo1bzfc">
              <li data-id="element-667" data-mp="1v0jdbutblfw2">
                <strong data-id="element-668" data-mp="1r126awy8waos">To provide the service</strong>, creating your account,
                running your kitties, recording expenses, balances and
                settlements. Lawful basis: performance of a contract with you.
              </li>
              <li data-id="element-669" data-mp="32ffr1sxpca0l">
                <strong data-id="element-670" data-mp="32fxvvdyfjwk4">To keep the service secure and working</strong>,
                diagnostics, abuse prevention and fixing faults. Lawful basis:
                our legitimate interests in running a secure, reliable service.
              </li>
              <li data-id="element-671" data-mp="292y79xutvjev">
                <strong data-id="element-672" data-mp="294j11l9biybo">To improve KittyPot</strong>, aggregate analytics and
                understanding how features are used. Lawful basis: our
                legitimate interests, or your consent where required for the
                technology used.
              </li>
              <li data-id="element-673" data-mp="1lddk12rn5zzs">
                <strong data-id="element-674" data-mp="1leyeksdyhixn">Contact matching, notifications and receipts</strong>,
                optional features you switch on. Lawful basis: your consent,
                given through the permission or setting.
              </li>
              <li data-id="element-675" data-mp="1u7i8jfjayzc5">
                <strong data-id="element-676" data-mp="1u81s10olpvsa">Product and launch emails you asked for</strong>,
                waitlist and product updates. Lawful basis: your consent.
              </li>
              <li data-id="element-677" data-mp="11ocwnj0exyaj">
                <strong data-id="element-678" data-mp="11nteq2arj9wi">Meeting our legal obligations</strong>, for example
                responding to a lawful request. Lawful basis: legal obligation.
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-679" data-mp="p83fnafesos9">
              13. Service providers
            </h2>
            <p data-id="element-680" data-mp="g6m5e8oonhbh">
              We use trusted service providers to operate KittyPot, for example
              to host our systems, deliver notifications and provide analytics.
              They process personal data on our instructions and under
              appropriate contractual terms, and they may not use it for their
              own purposes.
            </p>
            <p data-id="element-681" data-mp="1hzcf464ur1s">
              One provider is worth naming specifically. If you sign up to our
              waitlist on this website, your name, email address and marketing
              choice are submitted through Google infrastructure, which we use
              to collect and store waitlist entries. Google processes that
              submission on our behalf so we can contact you about the launch.
            </p>
            {/* INTERNAL: list the actual processors and their roles once
                confirmed. No providers are named that we cannot verify. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-682" data-mp="1dtlew7n2e5yp">
              14. International transfers
            </h2>
            <p data-id="element-683" data-mp="1irehkqp5shu8">
              Some of our service providers may process data outside the United
              Kingdom. Where that happens, we will put in place the safeguards
              required by UK data protection law so that your data continues to
              receive an appropriate level of protection.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-684" data-mp="zuda8z84u5n4">
              15. How long we keep data
            </h2>
            <p data-id="element-685" data-mp="2pal2wr2dpymx">
              We keep personal data only for as long as we need it for the
              purpose it was collected for, or for as long as we are required to
              keep it by law. Kitty and expense records are kept while the kitty
              is in use and afterwards so that members can refer back to a
              shared history. When data is no longer needed, we delete or
              anonymise it.
            </p>
            {/* INTERNAL: set and state specific retention periods for
                accounts, kitty history, receipts and diagnostics. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-686" data-mp="11azgpo44mq4f">
              16. Deleting your account
            </h2>
            <p data-id="element-687" data-mp="wcfwpt93foep">
              You can delete your account from within the app, or by emailing us
              from the address on your account. When you do:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-688" data-mp="16bx187clzw93">
              <li data-id="element-689" data-mp="3d1bm38gphnce">
                we remove or anonymise your account information where it is
                appropriate to do so
              </li>
              <li data-id="element-690" data-mp="383jt0b4brv6m">
                limited shared records may need to remain, in an anonymised
                form, so that other members' shared expense history and balances
                stay accurate
              </li>
              <li data-id="element-691" data-mp="5oo1jxlpt48w">
                we may retain limited information where we are required to by
                law
              </li>
            </ul>
            <p data-id="element-692" data-mp="fl14zn72awgu">
              Anything no longer needed for those purposes is deleted or
              anonymised. We cannot remove an expense from someone else's
              records where doing so would make their shared history wrong. If
              you would like to know what remains after deletion, email us and
              we will explain.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-693" data-mp="yb93frztgyqp">
              17. Your rights
            </h2>
            <p data-id="element-694" data-mp="13avga0bqgcyz">Under UK data protection law you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-695" data-mp="138itn9v15efl">
              <li data-id="element-696" data-mp="18a85grq2tw4q">ask for a copy of the personal data we hold about you</li>
              <li data-id="element-697" data-mp="2srr2e03v70st">ask us to correct information that is wrong</li>
              <li data-id="element-698" data-mp="2sp4lvayzxayg">ask us to delete your data, subject to section 16</li>
              <li data-id="element-699" data-mp="2nr302ia6qxja">ask us to restrict how we use your data</li>
              <li data-id="element-700" data-mp="2isi0ml2rchvw">
                object to processing we carry out on the basis of legitimate
                interests
              </li>
              <li data-id="element-701" data-mp="2dw1axmfwtvgp">
                ask for your data in a portable format, where that right applies
              </li>
              <li data-id="element-702" data-mp="3bjt9okum94qe">
                withdraw your consent at any time where we rely on consent
              </li>
            </ul>
            <p data-id="element-703" data-mp="31rnhpb4d6nb7">
              To exercise any of these, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-704" data-mp="31pszi4kh99at">
                {SUPPORT_EMAIL}
              </a>
              . If you are unhappy with how we have handled your data you can
              complain to the Information Commissioner's Office at ico.org.uk.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-705" data-mp="32kn6e2p3jy2q">
              18. Children
            </h2>
            <p data-id="element-706" data-mp="2ou9yu1g4aldx">
              KittyPot is intended for people who are old enough to use the
              service lawfully under the rules that apply to them, and it is not
              designed for children. We do not knowingly collect personal data
              from children. If you believe a child has created an account,
              contact us and we will look into it.
            </p>
            {/* INTERNAL: a specific minimum age is a business decision. Once
                set, state it here and make sure it matches the age rating
                declared on the App Store and Google Play. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-707" data-mp="1w3rz9aelr259">
              19. Security
            </h2>
            <p data-id="element-708" data-mp="215h5mcqvqrn0">
              We take reasonable technical and organisational measures to
              protect your data, including encrypted connections and access
              controls. No online service can promise perfect security, so we do
              not claim to be completely secure, but we work to keep your data
              safe and to respond quickly if something goes wrong.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-709" data-mp="1xjvxe4anthya">
              20. Changes to this policy
            </h2>
            <p data-id="element-710" data-mp="15v2lujv7csv1">
              We may update this policy as KittyPot develops. When we make a
              material change we will update the date at the top of this page
              and, where appropriate, let you know in the app.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-711" data-mp="1etxsty62m6ze">
              21. Contact us
            </h2>
            <p data-id="element-712" data-mp="r3tqdu4mcd9z">
              For any privacy question, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-713" data-mp="m61wwvobsj3o">
                {SUPPORT_EMAIL}
              </a>{' '}
              or write to us at the address in section 1.
            </p>
          </div>
        </div>
      </main>
      <Footer data-id="element-714" data-mp="3jqmbxy3p4qut" />
    </div>;
}